import { Injectable, signal } from '@angular/core';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
  /** Total time on screen, in ms */
  duration: number;
  /** Bumped when the same toast is shown again, so its countdown restarts */
  version: number;
  /** Playing its exit animation */
  leaving?: boolean;
}

/** How long each kind stays when the caller doesn't say: problems need more reading time */
const DEFAULT_DURATION: Record<ToastType, number> = {
  success: 3000,
  info: 4000,
  warning: 5000,
  error: 7000,
};
const MAX_VISIBLE = 4;
const EXIT_MS = 160;

interface Timer {
  handle: ReturnType<typeof setTimeout> | null;
  remaining: number;
  startedAt: number;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private toastsSignal = signal<Toast[]>([]);
  toasts = this.toastsSignal.asReadonly();

  private counter = 0;
  private timers = new Map<number, Timer>();

  show(message: string, type: ToastType = 'info', duration?: number) {
    const ms = duration ?? DEFAULT_DURATION[type];

    // The same message again (e.g. a repeated validation) refreshes the existing toast
    const existing = this.toastsSignal().find(
      (t) => !t.leaving && t.message === message && t.type === type,
    );
    if (existing) {
      this.toastsSignal.update((list) =>
        list.map((t) => (t.id === existing.id ? { ...t, duration: ms, version: t.version + 1 } : t)),
      );
      this.startTimer(existing.id, ms);
      return;
    }

    const id = ++this.counter;
    this.toastsSignal.update((list) => [...list, { id, message, type, duration: ms, version: 0 }]);
    this.startTimer(id, ms);

    // Keep the stack short: the oldest ones make room
    const visible = this.toastsSignal().filter((t) => !t.leaving);
    visible.slice(0, Math.max(0, visible.length - MAX_VISIBLE)).forEach((t) => this.remove(t.id));
  }

  /** Stops the countdown while the pointer or focus is on the toast */
  pause(id: number) {
    const timer = this.timers.get(id);
    if (!timer?.handle) return;
    clearTimeout(timer.handle);
    timer.handle = null;
    timer.remaining -= Date.now() - timer.startedAt;
  }

  resume(id: number) {
    const timer = this.timers.get(id);
    if (!timer || timer.handle) return;
    this.startTimer(id, Math.max(timer.remaining, 1000));
  }

  remove(id: number) {
    const timer = this.timers.get(id);
    if (timer?.handle) clearTimeout(timer.handle);
    this.timers.delete(id);

    this.toastsSignal.update((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => {
      this.toastsSignal.update((list) => list.filter((t) => t.id !== id));
    }, EXIT_MS);
  }

  private startTimer(id: number, ms: number) {
    const previous = this.timers.get(id);
    if (previous?.handle) clearTimeout(previous.handle);
    this.timers.set(id, {
      handle: setTimeout(() => this.remove(id), ms),
      remaining: ms,
      startedAt: Date.now(),
    });
  }
}
